import React, { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { animated, useSpring } from 'react-spring'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useAppContext, useSetAppContext } from '../../../../redux/slices/appContext'
import { useDashboardSettings, useGetViewConfigByPath } from '../GenericWheelProvider'
import { useDashboardNavigation } from '../NavigationProvider/context'
import AbundanceEngineView from '../../HomeDashboard/AbundanceEngineView'
import CurrentClient from '../../ClientDashboard/components/CurrentClient'
import ActionButtons from './ActionButtons'

const useStyles = makeStyles((theme) => ({
  container: ({ expanded }) => {
    const background = expanded
      ? {}
      : {
        backgroundPosition: 'bottom center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover'
      }
    return ({
      margin: 'inherit',
      padding: expanded ? '3.8rem 0 0' : '3.8rem 2.5rem 0rem',
      position: 'absolute',
      width: '100%',
      height: '100%',
      minHeight: '100%',
      top: 0,
      transition: 'padding .1s ease-in-out, border-color .1s ease-in-out, background-color .2s ease-in-out',
      ...background
    })
  },
  paper: ({ expanded }) => ({
    height: expanded ? '100%' : 'calc(100% - 1rem)',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    position: 'relative',
    backgroundColor: 'var(--tc-frame-bg-normal)',
    border: 'var(--tc-frame-border)',
    borderRadius: expanded ? '0' : '0.5rem'
  }),
  viewWrapper: {
    marginTop: ({ expanded }) => expanded ? '.8rem' : 0,
    height: '100%',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '1em',
      height: '1em',
      backgroundColor: 'transparent'
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: 'var(--border-radius-xl)',
      backgroundColor: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      visibility: 'visible',
      borderRadius: 'var(--border-radius-xl)',
      backgroundColor: 'var(--color-neutral-600)',
      border: '3px solid white'
    }
  },
  [theme.breakpoints.down('xs')]: {
    container: {
      padding: '50px 0 0 !important'
    },
    paper: {
      border: 'none !important',
      borderRadius: '0  !important'
    }
  }
}))

const SubViewFrame = ({
  classes,
  hideFrame,
  idle,
  expanded,
  onExpandToggle,
  viewPath
}) => {
  const navMan = useDashboardNavigation()
  const pageSprings = useSpring({
    from: { left: 100, opacity: 0 },
    to: { left: 0, opacity: 1 }
  })

  return (
    <animated.div className={clsx(hideFrame ? null : classes.paper, 'size-container')} style={pageSprings}>
      <ActionButtons
        idle={idle}
        expanded={expanded}
        onClose={() => navMan.goHome()}
        onExpandToggle={onExpandToggle}
        className={classes.actionButtons}
      />
      <div className={classes.viewWrapper}>
        <AbundanceEngineView viewPath={viewPath} />
      </div>
    </animated.div>
  )
}

SubViewFrame.propTypes = {
  hideFrame: PropTypes.bool,
  idle: PropTypes.bool,
  expanded: PropTypes.bool,
  onExpandToggle: PropTypes.func,
  viewPath: PropTypes.string,
  classes: PropTypes.object
}

const SubViewRoute = ({ showCurrentClient }) => {
  const { settings, idle, expanded, onExpandToggle } = useDashboardSettings()
  const { overContentOnDesktop = true } = settings

  const classes = useStyles({ expanded })
  const { view, subView } = useParams()
  const viewPath = useMemo(() => {
    if (typeof subView !== 'undefined') {
      return subView
    }
    if (typeof view !== 'undefined') {
      return view
    }
    return 'not-found'
  }, [view, subView])

  const { isFullViewOpen } = useAppContext()
  const setAppContext = useSetAppContext()
  useEffect(() => {
    setAppContext({ isFullViewOpen: isFullViewOpen })
    return () => setAppContext({ isFullViewOpen: false })
  }, [isFullViewOpen, setAppContext])

  useEffect(() => {
    setAppContext({ overContentOnDesktop, isSidebarOpen: false })
    return () => setAppContext({ overContentOnDesktop })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewPath, overContentOnDesktop])

  const { hideFrame, key } = useGetViewConfigByPath(subView)

  return (
    <>
      <div
        className={clsx('w-full h-full', {
          'sm:pl-5': !expanded,
          'sm:pr-5': !expanded,
          'sm:py-3': !expanded
        })}
        style={{
          willChange: 'padding, border-color, background-color',
          transition: 'padding .1s ease-in-out, border-color .1s ease-in-out, background-color .2s ease-in-out'
        }}
      >
        <SubViewFrame
          key={key}
          hideFrame={hideFrame}
          classes={classes}
          idle={idle}
          expanded={expanded}
          onExpandToggle={onExpandToggle}
          viewPath={viewPath}
        />
      </div>
      {showCurrentClient ? <CurrentClient /> : null}
    </>
  )
}

SubViewRoute.propTypes = {
  showCurrentClient: PropTypes.bool
}

export default SubViewRoute
